import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BookOpen,
  GraduationCap,
  Calendar,
  Clock,
  Star,
  LogOut,
  Home,
  ShoppingBag,
  Menu,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const StudentDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({ enrollments: 0, orders: 0 });
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, enrollmentsRes] = await Promise.all([
        axios.get(`${API}/dashboard/student-stats`),
        axios.get(`${API}/enrollments/my`)
      ]);
      setStats(statsRes.data);
      setEnrollments(enrollmentsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="student-dashboard">
      <SEO title="Student Dashboard" description="Track your learning progress at Sun Kamal Academy." />

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white border-b px-4 py-3 flex items-center justify-between">
        <button onClick={() => setSidebarOpen(true)} className="p-2 text-gray-700" aria-label="Open menu" data-testid="student-mobile-menu-toggle">
          <Menu className="w-6 h-6" />
        </button>
        <span className="font-bold text-lg">My Dashboard</span>
        <div className="w-10" />
      </div>

      {sidebarOpen && <div className="md:hidden dashboard-overlay" onClick={() => setSidebarOpen(false)} />}

      <div className="flex">
        {/* Sidebar */}
        <aside className={`fixed md:sticky top-0 left-0 z-40 w-64 bg-white border-r min-h-screen transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>
          <div className="p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <Link to="/" className="flex items-center gap-2">
                <img src="https://customer-assets.emergentagent.com/job_sunkamalplay/artifacts/oha9raa2_logo-kamal.webp" alt="Sun Kamal Academy" className="h-10 sm:h-12" />
              </Link>
              <button onClick={() => setSidebarOpen(false)} className="md:hidden p-1 text-gray-500" aria-label="Close menu">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="space-y-2">
              <div className="sidebar-link active" data-testid="nav-dashboard">
                <GraduationCap className="w-5 h-5" />
                <span>My Classes</span>
              </div>
              <Link to="/classes" className="sidebar-link" onClick={() => setSidebarOpen(false)}>
                <BookOpen className="w-5 h-5" />
                <span>Browse Classes</span>
              </Link>
              <Link to="/shop" className="sidebar-link" onClick={() => setSidebarOpen(false)}>
                <ShoppingBag className="w-5 h-5" />
                <span>Shop</span>
              </Link>
              <Link to="/" className="sidebar-link" onClick={() => setSidebarOpen(false)}>
                <Home className="w-5 h-5" />
                <span>Home</span>
              </Link>
              <button onClick={handleLogout} className="sidebar-link w-full text-left">
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6 md:p-8 pt-16 md:pt-8">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="dashboard-title">Welcome back, {user?.full_name}!</h1>
            <p className="text-gray-600 text-sm sm:text-base">Track your learning progress and continue your journey</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
            <Card data-testid="stat-enrolled-classes">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-base sm:text-lg">Enrolled Classes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl sm:text-4xl font-bold text-orange-600">{stats.enrollments}</div>
              </CardContent>
            </Card>
            <Card data-testid="stat-orders">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-base sm:text-lg">Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl sm:text-4xl font-bold text-blue-600">{stats.orders}</div>
              </CardContent>
            </Card>
            <Card data-testid="stat-progress">
              <CardHeader className="pb-2 sm:pb-4">
                <CardTitle className="text-base sm:text-lg">Average Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl sm:text-4xl font-bold text-green-600">
                  {enrollments.length > 0
                    ? Math.round(
                        enrollments.reduce((acc, e) => acc + (e.enrollment?.progress || 0), 0) /
                          enrollments.length
                      )
                    : 0}%
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="classes" className="w-full">
            <TabsList className="mb-4 sm:mb-6">
              <TabsTrigger value="classes" data-testid="tab-classes" className="text-xs sm:text-sm">My Classes</TabsTrigger>
              <TabsTrigger value="schedule" data-testid="tab-schedule" className="text-xs sm:text-sm">Schedule</TabsTrigger>
              <TabsTrigger value="progress" data-testid="tab-progress" className="text-xs sm:text-sm">Progress</TabsTrigger>
            </TabsList>

            <TabsContent value="classes">
              {loading ? (
                <div className="text-center py-12">Loading classes...</div>
              ) : enrollments.length === 0 ? (
                <Card className="p-8 sm:p-12 text-center">
                  <BookOpen className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg sm:text-xl font-bold mb-2">No classes enrolled yet</h3>
                  <p className="text-gray-600 mb-4 text-sm sm:text-base">Start your learning journey by enrolling in a class</p>
                  <Button onClick={() => navigate('/classes')} className="btn-primary">
                    Browse Classes
                  </Button>
                </Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-testid="enrolled-classes-grid">
                  {enrollments.map((enrollment, index) => (
                    <Card key={enrollment.enrollment?.id} className="hover:shadow-lg transition" data-testid={`enrolled-class-${index}`}>
                      <img
                        src={enrollment.class?.image}
                        alt={enrollment.class?.title}
                        className="w-full h-32 sm:h-40 object-cover rounded-t-lg"
                        loading="lazy"
                      />
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="px-2 py-1 bg-orange-100 text-orange-600 rounded text-xs font-medium">
                            {enrollment.class?.sport}
                          </span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded text-xs font-medium">
                            {enrollment.class?.skill_level}
                          </span>
                        </div>
                        <h3 className="text-base sm:text-lg font-bold mb-2">{enrollment.class?.title}</h3>
                        <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 flex-wrap">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {enrollment.class?.duration} min
                          </div>
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            {enrollment.class?.rating?.toFixed(1)}
                          </div>
                        </div>
                        <div className="mb-3">
                          <div className="flex justify-between text-xs sm:text-sm mb-1">
                            <span className="text-gray-600">Progress</span>
                            <span className="font-medium">{enrollment.enrollment?.progress || 0}%</span>
                          </div>
                          <Progress value={enrollment.enrollment?.progress || 0} className="h-2" />
                        </div>
                        <Button onClick={() => navigate(`/classes/${enrollment.class?.id}`)} className="w-full btn-primary text-sm">
                          Continue Learning
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="schedule">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg sm:text-xl">Upcoming Sessions</CardTitle>
                  <CardDescription className="text-sm">Your class schedule for the week</CardDescription>
                </CardHeader>
                <CardContent>
                  {enrollments.length === 0 ? (
                    <div className="text-center py-8 text-gray-600 text-sm">No scheduled sessions</div>
                  ) : (
                    <div className="space-y-3 sm:space-y-4">
                      {enrollments.slice(0, 5).map((enrollment, index) => (
                        <div key={index} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
                          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-lg flex items-center justify-center text-white font-bold text-lg sm:text-2xl shrink-0">
                            {enrollment.class?.sport?.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-sm sm:text-base truncate">{enrollment.class?.title}</h4>
                            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                              <Calendar className="w-4 h-4 shrink-0" />
                              <span className="truncate">{enrollment.class?.schedule}</span>
                            </div>
                          </div>
                          <Button size="sm" className="zoom-button text-xs sm:text-sm shrink-0">
                            Join Zoom
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="progress">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg sm:text-xl">Learning Progress</CardTitle>
                  <CardDescription className="text-sm">Track your achievements</CardDescription>
                </CardHeader>
                <CardContent>
                  {enrollments.length === 0 ? (
                    <div className="text-center py-8 text-gray-600 text-sm">No progress data available</div>
                  ) : (
                    <div className="space-y-4 sm:space-y-6">
                      {enrollments.map((enrollment, index) => (
                        <div key={index}>
                          <div className="flex justify-between mb-2">
                            <span className="font-medium text-sm sm:text-base">{enrollment.class?.title}</span>
                            <span className="text-gray-600 text-sm">{enrollment.enrollment?.progress || 0}%</span>
                          </div>
                          <Progress value={enrollment.enrollment?.progress || 0} className="h-2 sm:h-3" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default StudentDashboard;
